import XSelect from '@/Mantine/XSelect'
import XTextInput from '@/Mantine/XTextInput'
import { RootState } from '@/Store'
import { updateCurrentStep, updateLead } from '@/Store/leadFormSlice'
import { Button, Divider, HStack, VStack } from '@/XelaReact'
import { LoadingOverlay, SelectItem } from '@mantine/core'
import axios from 'axios'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

interface FormData {
  preferred_country: string
  preferred_country_code: string
  second_preferred_country: string
  second_preferred_country_code: string
  third_preferred_country: string
  third_preferred_country_code: string
  preferred_course_type: string
  preferred_course_name: string
  other_country: string
}

const LeadFormPreferenceDetail: FC<{ fields: string[]; requirements: string[]; agencyId: number }> = ({
  fields,
  requirements,
  agencyId,
}) => {
  const dispatch = useDispatch()
  const leadStore = useSelector((state: RootState) => state.leadForm)
  const [loading, setLoading] = useState(false)
  const [countries, setCountries] = useState<SelectItem[]>([])
  const [secondCountries, setSecondCountries] = useState<SelectItem[]>([])
  const [thirdCountries, setThirdCountries] = useState<SelectItem[]>([])
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [formData, setFormData] = useState<FormData>({
    preferred_country: '',
    preferred_country_code: '',
    second_preferred_country: '',
    second_preferred_country_code: '',
    third_preferred_country: '',
    third_preferred_country_code: '',
    preferred_course_type: '',
    preferred_course_name: '',
    other_country: '',
  })

  useEffect(() => {
    axios
      .post<Record<string, string>>('/guest/select/lead-meta-options', {
        entity_name: 'countries',
        column_name: 'country',
        with_key: true,
        agency_id: agencyId,
      })
      .then((response) => {
        const countryList = Object.keys(response.data).map((key) => {
          return {
            value: key,
            label: response.data[key],
          }
        })

        setCountries(countryList)
        setSecondCountries(countryList)
        setThirdCountries(countryList)
      })
      .catch((error) => {
        console.error('Error fetching countries:', error)
      })
  }, [])

  useEffect(() => {
    if (leadStore.lead) {
      const lead = leadStore.lead
      setFormData({
        preferred_country: lead.preferred_country || '',
        preferred_country_code: lead.preferred_country_code || '',
        second_preferred_country: lead.second_preferred_country || '',
        second_preferred_country_code: lead.second_preferred_country_code || '',
        third_preferred_country: lead.third_preferred_country || '',
        third_preferred_country_code: lead.third_preferred_country_code || '',
        preferred_course_type: lead.preferred_course_type || '',
        preferred_course_name: lead.preferred_course_name || '',
        other_country: lead.other_country || '',
      })
    }
  }, [leadStore.lead])

  const handleSubmit = async () => {
    if (leadStore.lead_id) {
      setLoading(true)
      try {
        const response = await axios.post(
          `/leads/form/${leadStore.lead_id}/preference-details`,
          {
            ...formData,
            uuid: leadStore.lead_form_uuid,
            csrfToken: leadStore.csrfToken,
          }
        )

        const { action, payload } = response.data

        if (action === 'preference_details:update') {
          if (payload.lead) {
            dispatch(updateLead(payload.lead))
            dispatch(updateCurrentStep(leadStore.current_step + 1))
          }
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          setErrors(error.response.data.errors || {})
        }
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <>
      {leadStore.is_mobile_view && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, width: '100vw', zIndex: 9999 }}>
          <HStack justifyContent="flex-end">
            <Button
              style={{
                borderRadius: 0,
                width: '100%',
                border: '1px solid #8E8C94 !important',
                background: '#FFFFFF',
                outline: 'none !important',
              }}
              label={'Back'}
              variant="secondary"
              onClick={() => {
                dispatch(updateCurrentStep(leadStore.current_step - 1))
              }}
            ></Button>
            <Button
              style={{
                borderRadius: 0,
                width: '100%',
                border: '1px solid #003CFF !important',
                outline: 'none !important',
              }}
              label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
              onClick={handleSubmit}
            ></Button>
          </HStack>
        </div>
      )}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ color: 'blueX' }} />
        <VStack spacing="12px" height="100%">
          <VStack
            spacing="12px"
            style={{
              height: '100%',
              overflowY: 'auto',
            }}
          >
            {fields.includes('preferred_country_code') && (
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                  <XSelect
                    required={requirements.includes('preferred_country_code')}
                    searchable
                    label={'Preferred Country'}
                    error={errors.preferred_country_code}
                    value={formData.preferred_country_code}
                    data={countries}
                    onChange={(value) => {
                      if (value) {
                        setFormData(prev => ({
                          ...prev,
                          preferred_country_code: value,
                          preferred_country: countries.find((country) => country.value === value)?.label as string,
                        }))
                      }
                    }}
                  />
                </HStack>
                <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                  <XSelect
                    required={requirements.includes('second_preferred_country_code')}
                    searchable
                    label={'Second Preferred Country'}
                    error={errors.second_preferred_country}
                    value={formData.second_preferred_country_code}
                    data={secondCountries}
                    onChange={(value) => {
                      if (value) {
                        setFormData(prev => ({
                          ...prev,
                          second_preferred_country_code: value,
                          second_preferred_country: secondCountries.find((country) => country.value === value)
                            ?.label as string,
                        }))
                      }
                    }}
                  />
                </HStack>
                <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                  <XSelect
                    required={requirements.includes('third_preferred_country_code')}
                    searchable
                    label={'Third Preferred Country'}
                    error={errors.third_preferred_country}
                    value={formData.third_preferred_country_code}
                    data={thirdCountries}
                    onChange={(value) => {
                      if (value) {
                        setFormData(prev => ({
                          ...prev,
                          third_preferred_country_code: value,
                          third_preferred_country: thirdCountries.find((country) => country.value === value)
                            ?.label as string,
                        }))
                      }
                    }}
                  />
                </HStack>
              </HStack>
            )}
            <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
              {fields.includes('preferred_course_type') && (
                <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                  <XSelect
                    required={requirements.includes('preferred_course_type')}
                    searchable
                    label={'Preferred Course Type'}
                    error={errors.preferred_course_type}
                    value={formData.preferred_course_type}
                    onInit={(callback) => {
                      axios
                        .post('/guest/select/lead-meta-options', {
                          entity_name: 'leads',
                          column_name: 'preferred_course_type',
                          agency_id: agencyId,
                        })
                        .then((response) => {
                          callback(response.data)
                        })
                        .catch((error) => {
                          console.log(error)
                          callback([])
                        })
                    }}
                    onChange={(value) => {
                      if (value) {
                        setFormData(prev => ({
                          ...prev,
                          preferred_course_type: value,
                        }))
                      }
                    }}
                  />
                </HStack>
              )}
              {fields.includes('preferred_course_name') && (
                <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                  <XTextInput
                    required={requirements.includes('preferred_course_name')}
                    label={'Preferred Course Name'}
                    error={errors.preferred_course_name}
                    value={formData.preferred_course_name}
                    onChange={(event) => {
                      setFormData(prev => ({
                        ...prev,
                        preferred_course_name: event.target.value,
                      }))
                    }}
                  />
                </HStack>
              )}
              {fields.includes('other_country') && (
                <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                  <XTextInput
                    required={requirements.includes('other_country')}
                    label={'Other Country'}
                    error={errors.other_country}
                    value={formData.other_country}
                    onChange={(event) => {
                      setFormData(prev => ({
                        ...prev,
                        other_country: event.target.value,
                      }))
                    }}
                  />
                </HStack>
              )}
            </HStack>
          </VStack>
          {!leadStore.is_mobile_view && (
            <>
              <HStack>
                <Divider variant={'dotted'}></Divider>
              </HStack>
              <HStack spacing={'16px'} justifyContent="flex-end">
                <Button
                  label={'Back'}
                  variant="secondary"
                  onClick={() => {
                    dispatch(updateCurrentStep(leadStore.current_step - 1))
                  }}
                ></Button>
                <Button
                  label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
                  onClick={handleSubmit}
                ></Button>
              </HStack>
            </>
          )}
        </VStack>
      </div>
    </>
  )
}

export default LeadFormPreferenceDetail
